if (!customElements.get('cart-discount-code')) {
  customElements.define(
    'cart-discount-code',
    class CartDiscountCode extends HTMLElement {
      connectedCallback() {
        if (this.initialized) return;
        this.initialized = true;

        this.input = this.querySelector('[data-cart-discount-input]');
        this.submitButton = this.querySelector('[data-cart-discount-submit]');
        this.message = this.querySelector('[data-cart-discount-message]');
        this.clearButtons = this.querySelectorAll('[data-cart-discount-clear]');
        this.applyLabel = this.submitButton?.textContent?.trim() || 'Apply';
        this.removeLabel = this.clearButtons[0]?.textContent?.trim() || 'Remove code';

        this.submitButton?.addEventListener('click', this.onSubmit.bind(this));
        this.input?.addEventListener('keydown', this.onInputKeydown.bind(this));
        this.clearButtons.forEach((button) => button.addEventListener('click', this.onClear.bind(this)));
      }

      onSubmit(event) {
        event.preventDefault();
        const discountCode = this.input?.value.trim();

        if (!discountCode) {
          this.showMessage(this.getDatasetString('emptyError'), 'error');
          this.input?.focus();
          return;
        }

        this.updateDiscount(discountCode, { validateCode: true });
      }

      onInputKeydown(event) {
        if (event.key !== 'Enter') return;

        event.preventDefault();
        this.onSubmit(event);
      }

      onClear(event) {
        event.preventDefault();
        this.updateDiscount('', { clearDiscounts: true });
      }

      async updateDiscount(discountCode, options = {}) {
        this.setBusy(true, options.clearDiscounts);
        this.showMessage('', 'neutral', true);

        try {
          const sectionsToRender = this.getSectionsToRender();
          const response = await fetch(routes.cart_update_url, {
            ...fetchConfig(),
            body: JSON.stringify({
              discount: discountCode,
              sections: [...new Set(sectionsToRender.map((section) => section.section))],
              sections_url: window.location.pathname,
            }),
          });
          const parsedState = await response.json();

          if (!response.ok || parsedState.status) throw new Error(parsedState.description || 'Cart update failed');

          if (options.validateCode && !this.hasAppliedDiscount(parsedState, discountCode)) {
            this.showMessage(this.getDatasetString('invalidError'), 'error');
            return;
          }

          this.renderSections(parsedState, sectionsToRender);
          this.dispatchCartChanged(parsedState);
          this.updateLiveRegion(
            options.clearDiscounts ? this.getDatasetString('removedMessage') : this.getDatasetString('appliedMessage')
          );
        } catch (error) {
          console.error(error);
          this.showMessage(this.getDatasetString('invalidError'), 'error');
        } finally {
          this.setBusy(false, options.clearDiscounts);
        }
      }

      hasAppliedDiscount(cartState, discountCode) {
        const enteredCodes = discountCode
          .split(',')
          .map((code) => code.trim().toLowerCase())
          .filter(Boolean);
        const appliedDiscounts = cartState.cart_level_discount_applications || [];

        return enteredCodes.some((code) =>
          appliedDiscounts.some((discount) => String(discount.title || '').trim().toLowerCase() === code)
        );
      }

      getSectionsToRender() {
        const mainCartSection = document.getElementById('main-cart-items')?.dataset.id;

        return [
          {
            id: 'main-cart-items',
            section: mainCartSection,
            selector: '#main-cart-items .js-contents',
          },
          {
            id: 'main-cart-footer',
            section: mainCartSection,
            selector: '#main-cart-footer .js-contents',
          },
          {
            id: 'cart-icon-bubble',
            section: 'cart-icon-bubble',
            selector: '.shopify-section',
          },
          {
            id: 'cart-live-region-text',
            section: 'cart-live-region-text',
            selector: '.shopify-section',
          },
        ].filter((section) => section.section);
      }

      renderSections(cartState, sectionsToRender) {
        if (!cartState.sections) {
          window.location.href = routes.cart_url;
          return;
        }

        sectionsToRender.forEach((section) => {
          const sectionHtml = cartState.sections[section.section];
          if (!sectionHtml) return;

          const target = this.getElementToReplace(section);
          const source = new DOMParser().parseFromString(sectionHtml, 'text/html').querySelector(section.selector);
          if (!target || !source) return;

          target.innerHTML = source.innerHTML;
        });
      }

      getElementToReplace(section) {
        const container = document.getElementById(section.id);
        if (!container) return null;

        const scopedTarget = container.querySelector(section.selector);
        if (scopedTarget) return scopedTarget;

        if (section.selector.startsWith(`#${section.id} `)) {
          return document.querySelector(section.selector) || container;
        }

        return container;
      }

      dispatchCartChanged(cartState) {
        window.dispatchEvent(
          new CustomEvent('northbound:cart-changed', {
            detail: {
              source: 'cart-discount-code',
              cartData: cartState,
            },
          })
        );
      }

      updateLiveRegion(message) {
        if (!message) return;

        const liveRegion = document.getElementById('cart-live-region-text');
        if (!liveRegion) return;

        liveRegion.textContent = message;
        liveRegion.setAttribute('aria-hidden', false);

        window.setTimeout(() => {
          liveRegion.setAttribute('aria-hidden', true);
        }, 1000);
      }

      showMessage(message, state = 'neutral', hidden = false) {
        if (!this.message) return;

        this.message.textContent = message || '';
        this.message.dataset.state = state;
        this.message.hidden = hidden || !message;
      }

      getDatasetString(key) {
        return this.decodeHtmlEntities(this.dataset[key] || '');
      }

      decodeHtmlEntities(value) {
        if (typeof value !== 'string' || !value.includes('&')) return value;

        const textarea = document.createElement('textarea');
        textarea.innerHTML = value;
        return textarea.value;
      }

      setBusy(isBusy, isClearing = false) {
        this.classList.toggle('is-busy', isBusy);

        if (this.input) this.input.disabled = isBusy;
        if (this.submitButton) {
          this.submitButton.disabled = isBusy;
          this.submitButton.textContent = isBusy ? this.getDatasetString('applyingLabel') : this.applyLabel;
        }

        this.clearButtons.forEach((button) => {
          button.disabled = isBusy;
          button.textContent = isBusy && isClearing ? this.getDatasetString('removingLabel') : this.removeLabel;
        });
      }
    }
  );
}
